import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Autocomplete,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './PaySlip.css';

const PaySlip = ({ token }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [payslipData, setPayslipData] = useState(null);

    const payslipRef = useRef();

    // API Base URL
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
    const API_BASE_1 = process.env.REACT_APP_API_BASE_1 || 'http://localhost:8000';
    const authToken = token || localStorage.getItem('authToken');

    // Autocomplete states
    const [payrollList, setPayrollList] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedPayroll, setSelectedPayroll] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [payrollInputValue, setPayrollInputValue] = useState('');
    const [employeeInputValue, setEmployeeInputValue] = useState('');

    // Go Back function
    const handleGoBack = () => {
        navigate('/payroll/register');
    };

    // Fetch payslip data
    const fetchPayslipData = useCallback(async (payrollId, empId) => {
        if (!authToken || !payrollId || !empId) return;

        try {
            setLoadingData(true);
            setError(null);

            const response = await fetch(`${API_BASE}payslip/get_payslip.php?payroll_id=${payrollId}&emp_id=${empId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error('Non-JSON response:', text);
                throw new Error('Server returned an invalid response.');
            }

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                setPayslipData(result.data);
                // Update URL with current selection
                setSearchParams({ payroll_id: payrollId, emp_id: empId });
            } else {
                setError(result.message || 'Failed to fetch payslip data');
                setPayslipData(null);
            }
        } catch (err) {
            setError(err.message || 'Network error occurred');
            setPayslipData(null);
            console.error('Error fetching payslip data:', err);
        } finally {
            setLoadingData(false);
        }
    }, [authToken, API_BASE, setSearchParams]);

    // Fetch employees for a specific payroll
    const fetchEmployeesForPayroll = useCallback(async (payrollId, selectEmpId = null) => {
        if (!authToken || !payrollId) return;

        try {
            const response = await fetch(`${API_BASE}payslip/get_employees_by_payroll.php?payroll_id=${payrollId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error('Non-JSON response:', text);
                throw new Error('Server returned an invalid response.');
            }

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                const employees = result.data.map(emp => ({
                    ...emp,
                    label: `${emp.emp_code} - ${emp.first_name} ${emp.last_name}`
                }));
                setEmployeeList(employees);

                // Select employee if emp_id is provided
                if (selectEmpId && employees.length > 0) {
                    const foundEmployee = employees.find(emp => String(emp.emp_id) === String(selectEmpId));
                    if (foundEmployee) {
                        setSelectedEmployee(foundEmployee);
                        fetchPayslipData(payrollId, selectEmpId);
                    }
                }
            } else {
                setError(result.message || 'Failed to fetch employees');
            }
        } catch (err) {
            console.error('Error fetching employees:', err);
            setError(err.message || 'Network error occurred');
        }
    }, [authToken, API_BASE, fetchPayslipData]);

    // Fetch payroll list for autocomplete
    const fetchPayrollList = useCallback(async () => {
        if (!authToken) {
            setError('Authentication token missing. Please log in again.');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE}payslip/get_payroll_list_for_payslip.php`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error('Non-JSON response:', text);
                throw new Error('Server returned an invalid response.');
            }

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                const payrolls = result.data.map(p => ({
                    ...p,
                    label: `${p.month_year} (ID: ${p.payroll_id})${p.unit_name ? ` - ${p.unit_name}` : ''}`
                }));
                setPayrollList(payrolls);

                // Check if payroll_id is in URL
                const payrollIdFromUrl = searchParams.get('payroll_id');
                const empIdFromUrl = searchParams.get('emp_id');

                if (payrollIdFromUrl) {
                    const foundPayroll = payrolls.find(p => String(p.payroll_id) === String(payrollIdFromUrl));
                    if (foundPayroll) {
                        setSelectedPayroll(foundPayroll);
                        if (empIdFromUrl) {
                            // Fetch employee list for this payroll and select employee
                            fetchEmployeesForPayroll(payrollIdFromUrl, empIdFromUrl);
                        }
                    }
                }
            } else {
                setError(result.message || 'Failed to fetch payroll list');
            }
        } catch (err) {
            console.error('Error fetching payroll list:', err);
            setError(err.message || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    }, [authToken, API_BASE, searchParams, fetchEmployeesForPayroll]);

    // Handle payroll selection
    const handlePayrollSelect = (event, newValue) => {
        setSelectedPayroll(newValue);
        setSelectedEmployee(null);
        setEmployeeList([]);
        setPayslipData(null);

        if (newValue) {
            fetchEmployeesForPayroll(newValue.payroll_id);
        }
    };

    // Handle employee selection
    const handleEmployeeSelect = (event, newValue) => {
        setSelectedEmployee(newValue);
        if (newValue && selectedPayroll) {
            fetchPayslipData(selectedPayroll.payroll_id, newValue.emp_id);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numericAmount);
    };

    // Construct full logo URL
    const getLogoUrl = () => {
        // Based on API: photo path is "uploads/company_logos/..."
        const photo = payslipData?.company?.photo || payslipData?.company?.company_logo;
        if (!photo) return null;

        if (photo.startsWith('http')) return photo;

        // Clean base and path to ensure single slash
        const base = (API_BASE_1 || '').replace(/\/+$/, '');
        const path = photo.replace(/^\/+/, '');
        return `${base}/${path}`;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Export to PDF with better handling
    const exportToPDF = () => {
        if (!payslipData) return;

        try {
            setExporting(true);
            const input = document.getElementById('payslip-content');

            // Set capture options to ensure desktop layout and high quality
            const options = {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: 1200, // Force desktop width for MUI breakpoints
                onclone: (clonedDoc) => {
                    const clonedInput = clonedDoc.getElementById('payslip-content');
                    if (clonedInput) {
                        clonedInput.style.width = '800px';
                        clonedInput.style.maxWidth = 'none';
                        clonedInput.style.margin = '0';
                        clonedInput.style.padding = '30px';
                        // Force background colors to be captured
                        clonedInput.style.backgroundColor = '#ffffff';
                    }
                }
            };

            html2canvas(input, options).then((canvas) => {
                const imgData = canvas.toDataURL('image/png', 1.0);
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();

                // Calculate image dimensions to fit A4 width
                const imgWidth = pageWidth;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                let heightLeft = imgHeight;
                let position = 0;

                // Add first page
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;

                // Add additional pages if needed
                while (heightLeft > 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                }

                // Save PDF
                const fileName = `Payslip_${payslipData.employee.emp_code}_${payslipData.payroll.month_year.replace(/\//g, '-')}.pdf`;
                pdf.save(fileName);

                setExporting(false);
            }).catch((error) => {
                console.error('Error in html2canvas:', error);
                alert('Failed to generate PDF. Please try again.');
                setExporting(false);
            });
        } catch (err) {
            console.error('Error exporting to PDF:', err);
            alert('Failed to export to PDF');
            setExporting(false);
        }
    };

    // Print function
    const handlePrint = () => {
        if (!payslipData) return;

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        const html = `
<!DOCTYPE html>
<html>
<head>
    <title>Payslip - ${payslipData.employee.first_name} ${payslipData.employee.last_name}</title>
    <style>
        @media print {
            @page { margin: 20px; }
            body { font-family: Arial, sans-serif; }
            .payslip-container { border: 2px solid #000; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 20px; }
            .header-content { flex: 1; text-align: center; }
            .header-content.with-logo { text-align: left; }
            .company-logo { max-width: 100px; max-height: 100px; object-fit: contain; }
            .company-name { font-size: 24px; font-weight: bold; color: #1976d2; }
            .section { margin-bottom: 20px; }
            .section-title { padding: 10px; font-weight: bold; margin-bottom: 10px; }
            .two-column-container { display: flex; gap: 20px; margin-bottom: 20px; }
            .column { flex: 1; }
            .amount { text-align: right; font-weight: bold; }
            .total-row { font-weight: bold; background-color: #e0e0e0; }
            .signature-area { margin-top: 50px; border-top: 2px dashed #ccc; padding-top: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 8px; border: 1px solid #ddd; }
        }
    </style>
</head>
<body>
    <div class="payslip-container">
        <!-- Company Header -->
        <div class="header">
            ${payslipData.company.photo ? `
                <img src="${getLogoUrl()}" class="company-logo" alt="Company Logo" onerror="this.style.display='none'">
            ` : ''}
            <div class="header-content ${payslipData.company.photo ? 'with-logo' : ''}">
                <div class="company-name">${payslipData.company.company_name}</div>
                <div>${payslipData.company.address}, ${payslipData.company.city}, ${payslipData.company.state}</div>
                <div>GST: ${payslipData.company.gst_number || 'N/A'}</div>
            </div>
        </div>
        
        <!-- Employee Details -->
        <div class="section">
            <div class="section-title">Employee Details</div>
            <table>
                <tr>
                    <td width="50%"><strong>Employee Code:</strong> ${payslipData.employee.emp_code}</td>
                    <td width="50%"><strong>Name:</strong> ${payslipData.employee.first_name} ${payslipData.employee.last_name}</td>
                </tr>
                <tr>
                    <td><strong>Department:</strong> ${payslipData.employee.department}</td>
                    <td><strong>Designation:</strong> ${payslipData.employee.designation}</td>
                </tr>
                <tr>
                    <td><strong>Bank:</strong> ${payslipData.employee.bank_name || 'N/A'}</td>
                    <td><strong>A/C No:</strong> ${payslipData.employee.bank_account || 'N/A'}</td>
                </tr>
            </table>
        </div>
        
        <!-- Pay Period -->
        <div class="section">
            <div class="section-title">Pay Period</div>
            <table>
                <tr>
                    <td width="33%"><strong>Month/Year:</strong> ${payslipData.payroll.month_year}</td>
                    <td width="33%"><strong>From:</strong> ${formatDate(payslipData.payroll.from_date)}</td>
                    <td width="33%"><strong>To:</strong> ${formatDate(payslipData.payroll.to_date)}</td>
                </tr>
                <tr>
                    <td><strong>Paid Days:</strong> ${payslipData.attendance.present_days}</td>
                    <td><strong>Effective Days:</strong> ${payslipData.attendance.effective_days}</td>
                    <td><strong>Payroll ID:</strong> ${payslipData.payroll.payroll_id}</td>
                </tr>
            </table>
        </div>
        
        <!-- Salary Details -->
        <div class="section">
            <div class="section-title">Salary Details</div>
            
            ${payslipData.general_components.length > 0 ? `
                <div style="margin-bottom: 20px;">
                    <div style="background-color: #f5f5f5; padding: 10px; font-weight: bold; margin-bottom: 10px;">
                        General Information
                    </div>
                    <div style="display: flex; gap: 20px;">
                        <div style="flex: 1;">
                            <table>
                                ${(() => {
                    const rows = [];
                    const half = Math.ceil(payslipData.general_components.length / 2);
                    for (let i = 0; i < half; i++) {
                        const comp = payslipData.general_components[i];
                        rows.push(`
                                            <tr>
                                                <td><strong>${comp.component_name.replace(/_/g, ' ')}:</strong></td>
                                                <td align="right">
                                                    ${comp.component_name.toLowerCase().includes('percentage')
                                ? comp.amount + '%'
                                : comp.component_name.toLowerCase().includes('ceiling')
                                    ? formatCurrency(comp.amount)
                                    : comp.amount}
                                                </td>
                                            </tr>
                                        `);
                    }
                    return rows.join('');
                })()}
                            </table>
                        </div>
                        <div style="flex: 1;">
                            <table>
                                ${(() => {
                    const rows = [];
                    const half = Math.ceil(payslipData.general_components.length / 2);
                    for (let i = half; i < payslipData.general_components.length; i++) {
                        const comp = payslipData.general_components[i];
                        rows.push(`
                                            <tr>
                                                <td><strong>${comp.component_name.replace(/_/g, ' ')}:</strong></td>
                                                <td align="right">
                                                    ${comp.component_name.toLowerCase().includes('percentage')
                                ? comp.amount + '%'
                                : comp.component_name.toLowerCase().includes('ceiling')
                                    ? formatCurrency(comp.amount)
                                    : comp.amount}
                                                </td>
                                            </tr>
                                        `);
                    }
                    return rows.join('');
                })()}
                            </table>
                        </div>
                    </div>
                </div>
            ` : ''}

            <!-- Two-column layout for Earnings and Deductions -->
            <div class="two-column-container">
                <!-- Earnings Column -->
                <div class="column">
                    <div style="background-color: #e8f5e8; padding: 10px; font-weight: bold; margin-bottom: 10px;">
                        Earnings
                    </div>
                    <table>
                        ${payslipData.earnings.map(earning => `
                            <tr>
                                <td>${earning.component_name.replace(/_/g, ' ')}</td>
                                <td class="amount">${formatCurrency(earning.amount)}</td>
                            </tr>
                        `).join('')}
                        <tr class="total-row">
                            <td><strong>Gross Pay</strong></td>
                            <td class="amount">${formatCurrency(summary.gross_pay)}</td>
                        </tr>
                    </table>
                </div>
                
                <!-- Deductions Column -->
                <div class="column">
                    <div style="background-color: #ffebee; padding: 10px; font-weight: bold; margin-bottom: 10px;">
                        Deductions
                    </div>
                    <table>
                        ${payslipData.deductions.map(deduction => `
                            <tr>
                                <td>${deduction.component_name.replace(/_/g, ' ')}</td>
                                <td class="amount">${formatCurrency(deduction.amount)}</td>
                            </tr>
                        `).join('')}
                        <tr class="total-row">
                            <td><strong>Total Deductions</strong></td>
                            <td class="amount">${formatCurrency(summary.total_deductions)}</td>
                        </tr>
                    </table>
                </div>
            </div>
            
            <!-- CTC Components (if any) -->
            ${payslipData.ctc_components.length > 0 ? `
                <div style="margin-top: 20px;">
                    <div style="background-color: #e3f2fd; padding: 10px; font-weight: bold; margin-bottom: 10px;">
                        CTC Components
                    </div>
                    <table>
                        ${payslipData.ctc_components.map(ctc => `
                            <tr>
                                <td>${ctc.component_name.replace(/_/g, ' ')}</td>
                                <td class="amount">${formatCurrency(ctc.amount)}</td>
                            </tr>
                        `).join('')}
                        <tr class="total-row">
                            <td><strong>CTC + Gross Pay</strong></td>
                            <td class="amount">${formatCurrency(summary.ctc_gross_total)}</td>
                        </tr>
                    </table>
                </div>
            ` : ''}
            
            <!-- Net Pay -->
            <div style="margin-top: 20px; background-color: #fff3e0; padding: 15px;">
                <table>
                    <tr>
                        <td width="70%"><strong style="font-size: 16px;">NET PAY</strong></td>
                        <td width="30%" align="right"><strong style="font-size: 16px; color: #d32f2f;">${formatCurrency(summary.net_pay)}</strong></td>
                    </tr>
                </table>
            </div>
        </div>
        
        <!-- Net Amount in Words -->
        <div style="text-align: center; margin-top: 10px;">
            <p><em>Net Amount in Words: ${numberToWords(summary.net_pay)} Rupees Only</em></p>
        </div>
        
        <!-- Signature Area -->
        <div class="signature-area">
            <table>
                <tr>
                    <td width="50%" align="center">
                        <div style="border-top: 1px solid #000; width: 200px; margin-top: 40px;"></div>
                        <div>Authorized Signatory</div>
                    </td>
                    <td width="50%" align="center">
                        <div style="border-top: 1px solid #000; width: 200px; margin-top: 40px;"></div>
                        <div>Employee Signature</div>
                    </td>
                </tr>
            </table>
            <div style="margin-top: 20px; font-size: 12px; text-align: center;">
                <div>This is a computer generated payslip. No signature required.</div>
                <div>Generated on: ${new Date().toLocaleDateString('en-IN')}</div>
            </div>
        </div>
    </div>
    
    <script>
        window.onload = function() {
            window.print();
            setTimeout(function() {
                window.close();
            }, 100);
        }
    </script>
</body>
</html>
        `;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
    };

    // Initialize on component mount
    useEffect(() => {
        if (authToken) {
            fetchPayrollList();
        } else {
            setError('Please login to access payslip');
            setLoading(false);
        }
    }, [authToken, fetchPayrollList]);

    // Calculate summary totals
    const summary = React.useMemo(() => {
        if (!payslipData) return null;

        const grossPay = payslipData.earnings.reduce((sum, earning) => sum + parseFloat(earning.amount || 0), 0);
        const ctcTotal = payslipData.ctc_components.reduce((sum, ctc) => sum + parseFloat(ctc.amount || 0), 0);
        const totalDeductions = payslipData.deductions.reduce((sum, deduction) => sum + parseFloat(deduction.amount || 0), 0);
        const netPay = grossPay - totalDeductions;

        return {
            gross_pay: grossPay,
            ctc_gross_total: grossPay + ctcTotal,
            total_deductions: totalDeductions,
            net_pay: netPay
        };
    }, [payslipData]);

    // Loading state
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header with Back Button */}
            <Card sx={{ mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={handleGoBack}
                                sx={{ mb: 2 }}
                                variant="outlined"
                                size="small"
                            >
                                Back to Payroll Register
                            </Button>
                            <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                EMPLOYEE PAYSLIP
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body2" color="text.secondary" align="right">
                                Generate individual payslip for employees
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Selection Card */}
            <Card sx={{ mb: 3, border: '2px solid #e0e0e0' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32' }}>
                        Select Payroll & Employee
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={payrollList}
                                getOptionLabel={(option) => option.label || ''}
                                value={selectedPayroll}
                                onChange={handlePayrollSelect}
                                inputValue={payrollInputValue}
                                onInputChange={(event, newInputValue) => {
                                    setPayrollInputValue(newInputValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Payroll Period"
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li key={key} {...optionProps}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body1">
                                                    {option.month_year}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {option.payroll_id} | {formatDate(option.from_date)} to {formatDate(option.to_date)}
                                                    {option.unit_name && ` | Unit: ${option.unit_name}`}
                                                </Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                                isOptionEqualToValue={(option, value) => option.payroll_id === value.payroll_id}
                                loading={loading}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={employeeList}
                                getOptionLabel={(option) => option.label || ''}
                                value={selectedEmployee}
                                onChange={handleEmployeeSelect}
                                inputValue={employeeInputValue}
                                onInputChange={(event, newInputValue) => {
                                    setEmployeeInputValue(newInputValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Employee"
                                        variant="outlined"
                                        fullWidth
                                        disabled={!selectedPayroll}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li key={key} {...optionProps}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                                                <Box>
                                                    <Typography variant="body1">
                                                        {option.first_name} {option.last_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.emp_code} | {option.department || 'N/A'} | {option.designation || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </li>
                                    );
                                }}
                                isOptionEqualToValue={(option, value) => option.emp_id === value.emp_id}
                                loading={loadingData}
                                disabled={!selectedPayroll || loadingData}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State for Payslip Data */}
            {loadingData && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            )}

            {/* Payslip Display */}
            {!loadingData && payslipData && (
                <Box ref={payslipRef}>
                    {/* Action Buttons */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }} className="action-buttons">
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            disabled={exporting}
                            sx={{ backgroundColor: '#1976d2' }}
                        >
                            Print
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={exportToPDF}
                            disabled={exporting}
                            sx={{ backgroundColor: '#d32f2f' }}
                        >
                            {exporting ? 'Exporting...' : 'Download PDF'}
                        </Button>
                    </Box>

                    {/* Payslip Content */}
                    <Box id="payslip-content" sx={{
                        border: '2px solid #000',
                        p: 3,
                        backgroundColor: 'white',
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        {/* Company Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, borderBottom: '2px solid #000', pb: 2 }}>
                            {payslipData.company.photo && (
                                <Box sx={{ mr: 3, display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        component="img"
                                        src={getLogoUrl()}
                                        alt="Company Logo"
                                        sx={{
                                            maxWidth: '120px',
                                            maxHeight: '80px',
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                        onError={(e) => {
                                            console.error('Logo failed to load:', e.target.src);
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </Box>
                            )}
                            <Box sx={{ flex: 1, textAlign: payslipData.company.photo ? 'left' : 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                                    {payslipData.company.company_name}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {payslipData.company.address && `${payslipData.company.address}, `}
                                    {payslipData.company.city && `${payslipData.company.city}, `}
                                    {payslipData.company.state && `${payslipData.company.state}`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {payslipData.company.gst_number && `GST: ${payslipData.company.gst_number}`}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Employee Details */}
                        <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: '#2e7d32' }}>
                                    Employee Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="body2"><strong>Employee Code:</strong> {payslipData.employee.emp_code}</Typography>
                                        <Typography variant="body2"><strong>Name:</strong> {payslipData.employee.first_name} {payslipData.employee.last_name}</Typography>
                                        <Typography variant="body2"><strong>Department:</strong> {payslipData.employee.department}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="body2"><strong>Designation:</strong> {payslipData.employee.designation}</Typography>
                                        <Typography variant="body2"><strong>Bank:</strong> {payslipData.employee.bank_name || 'N/A'}</Typography>
                                        <Typography variant="body2"><strong>A/C No:</strong> {payslipData.employee.bank_account || 'N/A'}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Pay Period */}
                        <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: '#2e7d32' }}>
                                    Pay Period
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="body2"><strong>Month/Year:</strong> {payslipData.payroll.month_year}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="body2"><strong>From:</strong> {formatDate(payslipData.payroll.from_date)}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="body2"><strong>To:</strong> {formatDate(payslipData.payroll.to_date)}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="body2"><strong>Paid Days:</strong> {payslipData.attendance.present_days}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="body2"><strong>Effective Days:</strong> {payslipData.attendance.effective_days}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="body2"><strong>Payroll ID:</strong> {payslipData.payroll.payroll_id}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Salary Details */}
                        <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: '#2e7d32' }}>
                                    Salary Details
                                </Typography>

                                {payslipData.general_components.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" sx={{ mb: 1, color: '#666', fontWeight: 'bold' }}>
                                            General Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TableContainer component={Paper} variant="outlined">
                                                    <Table size="small">
                                                        <TableBody>
                                                            {payslipData.general_components.slice(0, Math.ceil(payslipData.general_components.length / 2)).map((comp) => (
                                                                <TableRow key={comp.component_id}>
                                                                    <TableCell sx={{ fontWeight: 'bold', width: '60%', backgroundColor: '#f9f9f9' }}>
                                                                        {comp.component_name.replace(/_/g, ' ')}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {comp.component_name.toLowerCase().includes('percentage')
                                                                            ? `${comp.amount}%`
                                                                            : comp.component_name.toLowerCase().includes('ceiling')
                                                                                ? formatCurrency(comp.amount)
                                                                                : comp.amount}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TableContainer component={Paper} variant="outlined">
                                                    <Table size="small">
                                                        <TableBody>
                                                            {payslipData.general_components.slice(Math.ceil(payslipData.general_components.length / 2)).map((comp) => (
                                                                <TableRow key={comp.component_id}>
                                                                    <TableCell sx={{ fontWeight: 'bold', width: '60%', backgroundColor: '#f9f9f9' }}>
                                                                        {comp.component_name.replace(/_/g, ' ')}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {comp.component_name.toLowerCase().includes('percentage')
                                                                            ? `${comp.amount}%`
                                                                            : comp.component_name.toLowerCase().includes('ceiling')
                                                                                ? formatCurrency(comp.amount)
                                                                                : comp.amount}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Two-column layout for Earnings and Deductions */}
                                <div className="earnings-deductions-container">
                                    {/* Earnings Section */}
                                    <div className="earnings-section">
                                        <Typography variant="subtitle1" sx={{ mb: 1, color: '#2e7d32', fontWeight: 'bold' }}>
                                            Earnings
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: '#e8f5e8' }}>
                                                        <TableCell>Component</TableCell>
                                                        <TableCell align="right">Amount (₹)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {payslipData.earnings.map((earning) => (
                                                        <TableRow key={earning.component_id}>
                                                            <TableCell>{earning.component_name.replace(/_/g, ' ')}</TableCell>
                                                            <TableCell align="right">{formatCurrency(earning.amount)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow sx={{ backgroundColor: '#c8e6c9', fontWeight: 'bold' }}>
                                                        <TableCell>GROSS PAY</TableCell>
                                                        <TableCell align="right">{formatCurrency(summary.gross_pay)}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>

                                    {/* Deductions Section */}
                                    <div className="deductions-section">
                                        <Typography variant="subtitle1" sx={{ mb: 1, color: '#d32f2f', fontWeight: 'bold' }}>
                                            Deductions
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: '#ffebee' }}>
                                                        <TableCell>Component</TableCell>
                                                        <TableCell align="right">Amount (₹)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {payslipData.deductions.map((deduction) => (
                                                        <TableRow key={deduction.component_id}>
                                                            <TableCell>{deduction.component_name.replace(/_/g, ' ')}</TableCell>
                                                            <TableCell align="right">{formatCurrency(deduction.amount)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow sx={{ backgroundColor: '#ffccbc', fontWeight: 'bold' }}>
                                                        <TableCell>TOTAL DEDUCTIONS</TableCell>
                                                        <TableCell align="right">{formatCurrency(summary.total_deductions)}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                </div>

                                {/* CTC Components */}
                                {payslipData.ctc_components.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle1" sx={{ mb: 1, color: '#1976d2', fontWeight: 'bold' }}>
                                            CTC Components
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                        <TableCell>Component</TableCell>
                                                        <TableCell align="right">Amount (₹)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {payslipData.ctc_components.map((ctc) => (
                                                        <TableRow key={ctc.component_id}>
                                                            <TableCell>{ctc.component_name.replace(/_/g, ' ')}</TableCell>
                                                            <TableCell align="right">{formatCurrency(ctc.amount)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow sx={{ backgroundColor: '#bbdefb', fontWeight: 'bold' }}>
                                                        <TableCell>CTC + GROSS PAY</TableCell>
                                                        <TableCell align="right">{formatCurrency(summary.ctc_gross_total)}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}

                                {/* Net Pay */}
                                <Box sx={{ mt: 3 }} className="net-pay-section">
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow sx={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>
                                                    <TableCell sx={{ fontSize: '1.1rem', color: '#d32f2f' }}>NET PAY</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '1.1rem', color: '#d32f2f' }}>
                                                        {formatCurrency(summary.net_pay)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', textAlign: 'center' }}>
                                        Net Amount in Words: {numberToWords(summary.net_pay)} Rupees Only
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Signature Area */}
                        <Box sx={{ mt: 4, pt: 3, borderTop: '2px dashed #ccc' }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: 'center' }}>
                                    <Box sx={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto', pt: 1 }}>
                                        <Typography variant="body2">Authorized Signatory</Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: 'center' }}>
                                    <Box sx={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto', pt: 1 }}>
                                        <Typography variant="body2">Employee Signature</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    This is a computer generated payslip. No signature required.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Generated on: {new Date().toLocaleDateString('en-IN')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* No Data State */}
            {!loadingData && !payslipData && selectedPayroll && selectedEmployee && (
                <Alert severity="info" sx={{ mt: 3 }}>
                    No payslip data found for the selected employee and payroll period.
                </Alert>
            )}
        </Box>
    );
};

// Helper function to convert number to words
const numberToWords = (num) => {
    // Simplified number to words function
    // For production, use a proper library like 'number-to-words'
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (num === 0) return 'Zero';

    // Round to 2 decimal places
    num = Math.round(num * 100) / 100;

    // Convert to string and split integer and decimal parts
    const [integerPart, decimalPart] = num.toString().split('.');

    let words = '';
    let integerNum = parseInt(integerPart);

    if (integerNum >= 10000000) {
        words += numberToWords(Math.floor(integerNum / 10000000)) + ' Crore ';
        integerNum %= 10000000;
    }

    if (integerNum >= 100000) {
        words += numberToWords(Math.floor(integerNum / 100000)) + ' Lakh ';
        integerNum %= 100000;
    }

    if (integerNum >= 1000) {
        words += numberToWords(Math.floor(integerNum / 1000)) + ' Thousand ';
        integerNum %= 1000;
    }

    if (integerNum >= 100) {
        words += numberToWords(Math.floor(integerNum / 100)) + ' Hundred ';
        integerNum %= 100;
    }

    if (integerNum > 0) {
        if (words !== '') words += 'and ';

        if (integerNum < 10) {
            words += units[integerNum];
        } else if (integerNum < 20) {
            words += teens[integerNum - 10];
        } else {
            words += tens[Math.floor(integerNum / 10)];
            if (integerNum % 10 > 0) {
                words += ' ' + units[integerNum % 10];
            }
        }
    }

    // Handle decimal part (paise)
    if (decimalPart) {
        let decimalNum = parseInt(decimalPart.padEnd(2, '0'));
        if (decimalNum > 0) {
            words += ' and ' + numberToWords(decimalNum) + ' Paise';
        }
    }

    return words.trim();
};

export default PaySlip;